import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  jobId: string;
  orgId: string;
  tradeId?: string | null;
  onParsed: (claimEstimateId: string) => void;
}

export const ClaimImportDialog = ({ open, onOpenChange, jobId, orgId, tradeId, onParsed }: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setBusy(false); };

  const handleUpload = async () => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("PDF only");
      return;
    }
    setBusy(true);
    try {
      const path = `${orgId}/${jobId}/${Date.now()}_${file.name.replace(/[^\w.\-]/g, "_")}`;
      const up = await supabase.storage.from("claim-documents").upload(path, file, {
        contentType: "application/pdf",
        upsert: false,
      });
      if (up.error) throw up.error;

      const { data, error } = await supabase.functions.invoke("parse-adjuster-claim", {
        body: {
          job_id: jobId,
          org_id: orgId,
          trade_id: tradeId ?? null,
          storage_bucket: "claim-documents",
          storage_path: path,
          file_name: file.name,
        },
      });
      if (error) throw error;
      if (!data?.claim_estimate_id) throw new Error(data?.error || "Parser returned no estimate id");

      toast.success(`Parsed ${data.format} claim`, {
        description: `${data.item_count} line items, RCV $${(data.totals?.rcv ?? 0).toLocaleString()}`,
      });
      onParsed(data.claim_estimate_id);
      reset();
      onOpenChange(false);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Failed to parse claim");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import Adjuster Claim</DialogTitle>
          <DialogDescription>
            Upload a PDF from Xactimate, Symbility, Allstate, State Farm, or USAA. We'll auto-detect the format and extract line items.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="claim-pdf">Claim PDF</Label>
            <Input
              id="claim-pdf"
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={busy}
            />
          </div>

          {file && (
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-3 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="truncate">{file.name}</span>
              <span className="ml-auto text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</span>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button onClick={handleUpload} disabled={!file || busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {busy ? "Parsing…" : "Upload & Parse"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
